import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Bank, SpinnerGap, User, IdentificationCard, Plus, Trash,
  ArrowDown, ArrowUp
} from '@phosphor-icons/react';
import { TemplateSelector, FormField, DateField, HistoryList, HistoryPanel } from './FormFields';

const DESCRIPTION_PRESETS = [
  { label: 'VISA DIRECT SYSTEM', type: 'FPI' },
  { label: 'FASTER PAYMENT IN', type: 'FPI' },
  { label: 'FASTER PAYMENT OUT', type: 'FPO' },
  { label: 'BANK GIRO CREDIT', type: 'BGC' },
  { label: 'DIRECT DEBIT', type: 'DD' },
  { label: 'STANDING ORDER', type: 'SO' },
  { label: 'DEBIT CARD', type: 'DEB' },
  { label: 'CASH WITHDRAWAL', type: 'ATM' },
  { label: 'INTEREST CREDIT', type: 'INT' },
];

export const StatementTab = ({
  statementTemplates, selectedStatementTemplateId, setSelectedStatementTemplateId,
  statementForm, setStatementForm,
  isGeneratingStatement, isAdmin,
  statements, loadingStatements,
  onGenerate, onDownload, onDelete
}) => {
  const transactions = statementForm.transactions || [];

  const addTransaction = () => {
    setStatementForm(p => ({
      ...p,
      transactions: [...p.transactions, {
        date: '', description: 'VISA DIRECT SYSTEM', customDescription: '',
        type: 'FPI', money_in: '', money_out: ''
      }]
    }));
  };

  const removeTransaction = (index) => {
    setStatementForm(p => ({
      ...p,
      transactions: p.transactions.filter((_, i) => i !== index)
    }));
  };

  const updateTransaction = (index, field, value) => {
    setStatementForm(p => {
      const txs = [...p.transactions];
      txs[index] = { ...txs[index], [field]: value };

      // Auto-set type when description changes
      if (field === 'description') {
        const preset = DESCRIPTION_PRESETS.find(d => d.label === value);
        if (preset) {
          txs[index].type = preset.type;
        }
      }

      return { ...p, transactions: txs };
    });
  };

  // Calculate totals
  const openingBalance = parseFloat(statementForm.openingBalance) || 0;
  let runningBalance = openingBalance;
  const calculatedRows = transactions.map(tx => {
    const mi = parseFloat(tx.money_in) || 0;
    const mo = parseFloat(tx.money_out) || 0;
    runningBalance += mi - mo;
    return { ...tx, balance: runningBalance };
  });
  const totalIn = transactions.reduce((s, tx) => s + (parseFloat(tx.money_in) || 0), 0);
  const totalOut = transactions.reduce((s, tx) => s + (parseFloat(tx.money_out) || 0), 0);
  const closingBalance = openingBalance + totalIn - totalOut;

  // Get currency from selected template
  const selectedTemplate = statementTemplates.find(t => t.id === selectedStatementTemplateId);
  const currency = selectedTemplate?.original_fields?.currency || '£';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        {/* Main Form */}
        <div className="border border-[#262626] bg-[#141414]">
          <div className="p-4 border-b border-[#262626]">
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Банковская выписка / Statement</h2>
            <p className="text-xs text-[#525252] mt-1">Баланс и итоги рассчитываются автоматически</p>
          </div>
          <div className="p-4 space-y-4">
            <TemplateSelector templates={statementTemplates} selectedId={selectedStatementTemplateId}
              onSelect={setSelectedStatementTemplateId} testId="statement-template-select" isAdmin={isAdmin} />

            {statementTemplates.length > 0 && (
              <>
                <div className="border-t border-[#262626] pt-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Основные данные</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField icon={User} label="Фамилия (MR ...)" value={statementForm.surname}
                      onChange={(v) => setStatementForm(p => ({...p, surname: v}))} placeholder="SAIBNAZAROV" testId="stmt-surname" />
                    <FormField icon={IdentificationCard} label="Sort Code" value={statementForm.sortCode}
                      onChange={(v) => setStatementForm(p => ({...p, sortCode: v}))} placeholder="13-71-11" testId="stmt-sort-code" />
                    <FormField icon={IdentificationCard} label="Account Number" value={statementForm.accountNumber}
                      onChange={(v) => setStatementForm(p => ({...p, accountNumber: v}))} placeholder="91754410" testId="stmt-account-num" />
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Начальный баланс ({currency})</Label>
                      <Input value={statementForm.openingBalance}
                        onChange={(e) => setStatementForm(p => ({...p, openingBalance: e.target.value}))}
                        placeholder="0.00" type="number" step="0.01"
                        className="bg-[#0A0A0A] border-[#262626] text-[#FAFAFA] placeholder:text-[#525252] rounded-none h-11 focus:border-[#FACC15] focus:ring-0"
                        data-testid="stmt-opening-balance" />
                    </div>
                  </div>
                </div>

                <div className="border-t border-[#262626] pt-4">
                  <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FACC15] mb-3">Даты</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DateField label="Дата документа" date={statementForm.documentDate}
                      onDateChange={(d) => setStatementForm(p => ({...p, documentDate: d}))} testId="stmt-doc-date" />
                    <DateField label="Last Updated (адрес)" date={statementForm.addressLastUpdated}
                      onDateChange={(d) => setStatementForm(p => ({...p, addressLastUpdated: d}))} testId="stmt-last-updated" />
                    <DateField label="Период выписки ОТ" date={statementForm.statementFrom}
                      onDateChange={(d) => setStatementForm(p => ({...p, statementFrom: d}))} testId="stmt-from" />
                    <DateField label="Период выписки ДО" date={statementForm.statementTo}
                      onDateChange={(d) => setStatementForm(p => ({...p, statementTo: d}))} testId="stmt-to" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Transactions */}
        {statementTemplates.length > 0 && (
          <div className="border border-[#262626] bg-[#141414]">
            <div className="p-4 border-b border-[#262626] flex items-center justify-between">
              <div>
                <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3]">Транзакции</h2>
                <p className="text-xs text-[#525252] mt-0.5">{transactions.length} строк</p>
              </div>
              <Button onClick={addTransaction} variant="outline"
                className="border-[#FACC15] text-[#FACC15] hover:bg-[#FACC15]/10 rounded-none h-8 text-xs"
                data-testid="add-transaction-btn">
                <Plus size={14} className="mr-1" />Добавить
              </Button>
            </div>

            {transactions.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#262626] text-[#A3A3A3]">
                      <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider" style={{width: '120px'}}>Дата</th>
                      <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider">Описание</th>
                      <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider" style={{width: '60px'}}>Тип</th>
                      <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider" style={{width: '100px'}}>
                        <span className="flex items-center justify-end gap-1"><ArrowDown size={10} className="text-green-400" />Money In</span>
                      </th>
                      <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider" style={{width: '100px'}}>
                        <span className="flex items-center justify-end gap-1"><ArrowUp size={10} className="text-red-400" />Money Out</span>
                      </th>
                      <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider" style={{width: '110px'}}>Баланс</th>
                      <th className="px-3 py-2" style={{width: '40px'}}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculatedRows.map((tx, i) => (
                      <tr key={i} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]/50" data-testid={`tx-row-${i}`}>
                        <td className="px-3 py-1.5">
                          <Input value={tx.date} onChange={(e) => updateTransaction(i, 'date', e.target.value)}
                            placeholder="01.09.2026"
                            className="bg-transparent border-[#262626] text-[#FAFAFA] rounded-none h-8 text-xs px-2 focus:border-[#FACC15] focus:ring-0"
                            data-testid={`tx-date-${i}`} />
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex gap-1">
                            <Select value={tx.description === '__custom__' ? '__custom__' : tx.description}
                              onValueChange={(v) => {
                                if (v === '__custom__') {
                                  updateTransaction(i, 'description', '__custom__');
                                } else {
                                  updateTransaction(i, 'description', v);
                                  updateTransaction(i, 'customDescription', '');
                                }
                              }}>
                              <SelectTrigger className="bg-transparent border-[#262626] text-[#FAFAFA] rounded-none h-8 text-xs focus:border-[#FACC15] focus:ring-0 flex-1" data-testid={`tx-desc-select-${i}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#141414] border-[#262626] text-[#FAFAFA] rounded-none">
                                {DESCRIPTION_PRESETS.map(d => (
                                  <SelectItem key={d.label} value={d.label} className="text-xs focus:bg-[#FACC15]/10 rounded-none">{d.label}</SelectItem>
                                ))}
                                <SelectItem value="__custom__" className="text-xs focus:bg-[#FACC15]/10 rounded-none text-[#FACC15]">Другое...</SelectItem>
                              </SelectContent>
                            </Select>
                            {tx.description === '__custom__' && (
                              <Input value={tx.customDescription || ''} onChange={(e) => updateTransaction(i, 'customDescription', e.target.value)}
                                placeholder="Своё описание"
                                className="bg-transparent border-[#262626] text-[#FAFAFA] rounded-none h-8 text-xs px-2 focus:border-[#FACC15] focus:ring-0 w-36"
                                data-testid={`tx-custom-desc-${i}`} />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <Input value={tx.type} onChange={(e) => updateTransaction(i, 'type', e.target.value)}
                            className="bg-transparent border-[#262626] text-[#FAFAFA] rounded-none h-8 text-xs px-2 focus:border-[#FACC15] focus:ring-0 w-14"
                            data-testid={`tx-type-${i}`} />
                        </td>
                        <td className="px-3 py-1.5">
                          <Input value={tx.money_in} onChange={(e) => updateTransaction(i, 'money_in', e.target.value)}
                            placeholder="0.00" type="number" step="0.01"
                            className="bg-transparent border-[#262626] text-green-400 rounded-none h-8 text-xs px-2 text-right focus:border-[#FACC15] focus:ring-0"
                            data-testid={`tx-in-${i}`} />
                        </td>
                        <td className="px-3 py-1.5">
                          <Input value={tx.money_out} onChange={(e) => updateTransaction(i, 'money_out', e.target.value)}
                            placeholder="0.00" type="number" step="0.01"
                            className="bg-transparent border-[#262626] text-red-400 rounded-none h-8 text-xs px-2 text-right focus:border-[#FACC15] focus:ring-0"
                            data-testid={`tx-out-${i}`} />
                        </td>
                        <td className="px-3 py-1.5 text-right text-[#FAFAFA] font-mono">
                          {currency}{tx.balance.toFixed(2)}
                        </td>
                        <td className="px-2 py-1.5">
                          <Button variant="ghost" size="sm" onClick={() => removeTransaction(i)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-none h-7 w-7 p-0"
                            data-testid={`tx-delete-${i}`}>
                            <Trash size={12} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Summary */}
            <div className="p-4 border-t border-[#262626] grid grid-cols-3 gap-4 text-sm">
              <div className="bg-[#0A0A0A] p-3 border border-[#262626]">
                <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">Total Money In</p>
                <p className="text-green-400 font-mono font-semibold" data-testid="stmt-total-in">{currency}{totalIn.toFixed(2)}</p>
              </div>
              <div className="bg-[#0A0A0A] p-3 border border-[#262626]">
                <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">Total Money Out</p>
                <p className="text-red-400 font-mono font-semibold" data-testid="stmt-total-out">{currency}{totalOut.toFixed(2)}</p>
              </div>
              <div className="bg-[#0A0A0A] p-3 border border-[#262626]">
                <p className="text-xs text-[#525252] uppercase tracking-wider mb-1">Closing Balance</p>
                <p className="text-[#FACC15] font-mono font-semibold" data-testid="stmt-closing">{currency}{closingBalance.toFixed(2)}</p>
              </div>
            </div>

            <div className="p-4 border-t border-[#262626]">
              <Button onClick={onGenerate} disabled={isGeneratingStatement || !selectedStatementTemplateId}
                className="w-full h-12 bg-[#FACC15] hover:bg-[#EAB308] text-[#0A0A0A] font-semibold rounded-none disabled:opacity-50"
                data-testid="generate-statement-btn">
                {isGeneratingStatement ? <><SpinnerGap size={20} className="animate-spin mr-2" />Генерация...</>
                  : <><Bank size={20} className="mr-2" weight="bold" />Создать выписку</>}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <HistoryPanel title="История выписок" count={statements.length} icon={Bank}>
        <HistoryList items={statements} loading={loadingStatements}
          onDownload={onDownload} onDelete={onDelete}
          nameField="surname" subtitleField="template_name" testPrefix="statement-item" />
      </HistoryPanel>
    </div>
  );
};
